import { Injectable } from '@angular/core';
import { Functions } from '@angular/fire/functions';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { ModalController, NavController } from '@ionic/angular';
import { SopoLoadingService } from '../../shared/services/sopo-loading/sopo-loading.service';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { ProfileStateDispatcher } from '../state/profile/profile.dispatcher';
import { doc, Firestore, setDoc, collection, collectionData, CollectionReference, getDoc, DocumentReference, deleteDoc, query, where, docData, Timestamp } from '@angular/fire/firestore';
import { Store } from '@ngrx/store';
import { AppState } from '../state/app.state';
import { CachingStatus, CryptoState, HealthyToken, NftAsset, Token } from '../state/crypto/crypto.state';
import { addCollections, connectWallet, disconnectWallet, loadNftAssets, nftAssetsLoaded, setLoadingStatus } from '../state/crypto/crypto.actions';
import { CryptoStateDispatcher } from '../state/crypto/crypto.dispatcher';
import { firstValueFrom, from, Observable, of, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from '../backend/api.service';
import Metamask from 'src/app/plugins/metamask.plugin';
import { DEFAULT_DISMISS_AFTER_TIME, SopoPopupService, ViewContainerName } from 'src/app/sopo-popup/service/sopo-popup.service';
import { FeatureService } from './feature.service';

export const SOLANA_CHAIN_ID = '101';

@Injectable({
    providedIn: 'root'
})
export class CryptoService {

    nftAssetsSubscription: Subscription;
    enablePhantom = false;

    constructor(
        public loadingCtrl: SopoLoadingService,
        public modalCtrl: ModalController,
        public navCtrl: NavController,
        public http: HttpClient,
        public profileDispatch: ProfileStateDispatcher,
        public firestore: Firestore,
        public cloudFNs: Functions,
        private store: Store<AppState>,
        private cryptoStateDispatcher: CryptoStateDispatcher,
        private apiService: ApiService,
        private sopoPopupService: SopoPopupService,
        private featureService: FeatureService,
    ) { 
        this.store.select('auth').pipe(
            distinctUntilChanged((curr, prev) => curr.isAuthenticated === prev.isAuthenticated)
        ).subscribe(state => {
            if (state.isAuthenticated) this.checkOwnership(state.userID);
            else this.disinit();
        })
        this.store.select('crypto').pipe(
            distinctUntilChanged((prev, curr) => {
                return prev.address === curr.address;
            }),
            switchMap<CryptoState, Observable<IWalletStatus>>(cryptoState => {
                if (cryptoState.chainId !== SOLANA_CHAIN_ID) return of({
                    phantomEnabled: null,
                    address: cryptoState.address,
                    chainId: cryptoState.chainId
                });
                return this.featureService.phantomEnabled$.pipe(
                    map(phantomEnabled => {
                        return {
                        phantomEnabled,
                        address: cryptoState.address,
                        chainId: cryptoState.chainId
                        }
                    })
                );
            }),
        ).subscribe((walletStatus) => {
            if (walletStatus.address !== null) {
                if (walletStatus.phantomEnabled === false && walletStatus.chainId === SOLANA_CHAIN_ID) {
                    console.debug(`[CryptoService] - automatic disconnect! - phantomEnabled: ${walletStatus.phantomEnabled}`);
                    this.disconnectPhantom();
                    return;
                }

                this.store.dispatch(loadNftAssets());
                this.getCollections();
            }
        })
    }

    disinit() {
        this.unsubscribeFromNftAssets();
        this.cryptoStateDispatcher.clearCryptoState();
    }

    async checkOwnership(userId: string) {
        try {
            await this.apiService.onCheckNftOnwership(userId);
        } catch(error) {
            console.error("couldnt verify ownership", error);
        }
    }

    async connectToMetamask(viewContainerName?: ViewContainerName){
        this.loadingCtrl.present('profile');
        try {
            const result = await Metamask.connect();
            this.store.dispatch(connectWallet({
                provider: 'metamask_sdk',
                chainId: result.chainId,
                address: result.address,
                chain: 'ethereum',
                session: null,
                connectedAt: Timestamp.now(),
            }));
        } catch (error) {
            const message: string = error.message ? error.message : '';
            if (message.toLowerCase().includes('please wait')) {
                if (viewContainerName!!) {
                    this.sopoPopupService.showAlert('app', {
                        header: 'Alert',
                        message: 'Request already pending, please check metamask extension.',
                        dismissAfter: DEFAULT_DISMISS_AFTER_TIME,
                    })
                } else {
                    this.sopoPopupService.showAlert('app', {
                        header: 'Alert',
                        message: 'Request already pending, please check metamask extension.',
                    })
                }
            }
            console.error(error);
        }
        this.loadingCtrl.dismiss('profile');
    }

    async disconnectFromMetamask() {
        this.store.dispatch(disconnectWallet({ chain: 'ethereum' }));
    }

    private getAddress(): Promise<string> {
        return firstValueFrom(this.cryptoStateDispatcher.getAddress().pipe(filter(address => typeof address === 'string')));
    }

    private getAddressAndChain(): Promise<{ address: string, chain: ChainType }> {
        return firstValueFrom(this.cryptoStateDispatcher.getState().pipe(
            filter(state => typeof state.address === 'string' && typeof state.chainId === 'string'),
            map(state => {
                const chain: ChainType = state.chainId === SOLANA_CHAIN_ID ? 'svm' : 'evm';
                return {
                    address: state.address,
                    chain,
                }
            })
        ));
    }

    public async getChain(): Promise<ChainType> {
        const chainId = await firstValueFrom(this.cryptoStateDispatcher.getChainId().pipe(filter(chainId => typeof chainId === 'string')));
        return chainId === SOLANA_CHAIN_ID ? 'svm' : 'evm';
    }

    subscribeToNftAssets(){
        if (this.nftAssetsSubscription === undefined || this.nftAssetsSubscription.closed) {
            this.nftAssetsSubscription = this.getOwnedNftList().subscribe(nfts => {
                this.store.dispatch(nftAssetsLoaded({ nftAssets: nfts }));
            });
            const statusSub = this.getCachingStatus().subscribe(status => {
                this.store.dispatch(setLoadingStatus({ status }));
            });
            this.nftAssetsSubscription.add(statusSub);
        }
    }

    unsubscribeFromNftAssets(){
        if (this.nftAssetsSubscription) this.nftAssetsSubscription.unsubscribe();
    }

    getCachingStatus() {
        return from(this.getAddress()).pipe(
            switchMap(address => {
                const cachingStatusColRef = collection(this.firestore, 'tokenCacheStatus');
                return docData(doc(cachingStatusColRef, address));
            }),
            map(statusDoc => {
                let status = CachingStatus.NOT_CACHED;
                if (statusDoc && statusDoc.status) status = statusDoc.status;
                return status;                
            })
        );
    }

    public async dispatchCheckOwnedNft(): Promise<void> {
        console.debug('[CryptoService] - dispatchCheckOwnedNft');

        const aggregatorUrl: string = environment.nftAggregatorUrl;
        if (aggregatorUrl === undefined) throw new Error('No aggregator URL!');
        
        try {
            const { address, chain } = await this.getAddressAndChain();
    
            const params = new HttpParams().set('chain', chain);
    
            await firstValueFrom(this.http.get(`${aggregatorUrl}/account/${address}/nfts`, { params }));
        } catch (error) {
            console.error('[CryptoService] - dispatchCheckOwnedNft', error);
        }
    }

    preloadNft(nft: NftAsset): Promise<void> {
        return new Promise(async (resolve, reject) => {
            // const cache = await caches.open('assets');
            // try {
            //     const request = new Request(nft.imageUrl)
            //     const response = await fetch(request)
            //     const clonedResponse = response.clone()
            //     const blob = await response.blob();
            //     if (blob.type.includes('image')) {
            //         await cache.put(request, clonedResponse);
            //         resolve();
            //     }
            //     else {
            //         reject('not an image');
            //     }
            // } catch (e) {
                
            // }
            const tempImage: HTMLImageElement = new Image();
            tempImage.onload = () => {resolve()};
            tempImage.onerror = (e) => {reject(e)};
            tempImage.src = nft.imageUrl;
        })
    }

    getOwnedNftList(): Observable<NftAsset[]> {
        return from(this.getAddress()).pipe(
            switchMap(address => {
                const tokenColRef = collection(this.firestore, 'tokens') as CollectionReference<Token>;
                const tokenQuery = query(tokenColRef, where('owner', '==', address));
                return collectionData<Token>(tokenQuery).pipe(
                    map(tokens => {
                        let nfts: NftAsset[] = [];
                        for (let token of tokens) {
                            if (token.isHealthy === false) continue;
                            let mappedToken: NftAsset
                            mappedToken = {
                                name: token.metadata.name ? token.metadata.name : null,
                                collectionName: this.resolveCollectionName(token),
                                collectionAddress: token.collectionAddress,
                                collectionTokenId: token.tokenId,
                                imageUrl: token.imageUrl,
                                traits: token.metadata.attributes ? token.metadata.attributes : null,
                                type: token.type
                            }
                            nfts.push(mappedToken);
                        }
                        return nfts;
                    })
                );
            })
        )
    }

    resolveCollectionName(token: HealthyToken) {
        if (token.name) return token.name.split('#')[0];
        return '';
    }

    async addNftAsFavourite(nft: NftAsset) {
        const userId = await this.profileDispatch.getMyID();
        const collectionRef = collection(this.firestore, `users/${userId}/favoriteNfts`);
        const newDocRef = doc(collectionRef, `${nft.collectionAddress}_${nft.collectionTokenId}`);
        await setDoc(newDocRef, nft);
    }

    getFavorites(): Observable<NftAsset[]> {
        return from(this.profileDispatch.getMyID()).pipe(
            switchMap(userId => {
                const collectionRef = collection(this.firestore, `users/${userId}/favoriteNfts`);
                return collectionData<NftAsset>(collectionRef as CollectionReference<NftAsset>);
            })
        )
    }

    async checkNftAsFavorite(nft: NftAsset): Promise<boolean> {
        const userId = await this.profileDispatch.getMyID()
        const collectionRef = collection(this.firestore, `users/${userId}/favoriteNfts`);
        const docRef = doc(collectionRef, `${nft.collectionAddress}_${nft.collectionTokenId}`);
        const favoriteNftDoc = await getDoc<NftAsset, any>(docRef as DocumentReference<NftAsset>);
        return favoriteNftDoc.exists() as unknown as boolean;
    }

    async removeFromFavorites(nft: NftAsset): Promise<void> {
        const userId = await this.profileDispatch.getMyID()
        const collectionRef = collection(this.firestore, `users/${userId}/favoriteNfts`);
        const docRef = doc(collectionRef, `${nft.collectionAddress}_${nft.collectionTokenId}`);
        await deleteDoc(docRef);
    }

    private async getCollections() {
        const aggregatorUrl = environment.nftAggregatorUrl;
        if (aggregatorUrl === undefined) throw new Error('No aggregator URL!');

        const { address, chain } = await this.getAddressAndChain();

        let collectionList = [];
        if (chain === 'evm') {
            collectionList = await this.getCollectionsEVM(chain, address, aggregatorUrl);
        } else {
            collectionList = await this.getCollectionsSVM(chain, address, aggregatorUrl);
        }

        this.store.dispatch(addCollections({collections: collectionList}));
    }

    private async getCollectionsEVM(chain: ChainType, walletAddress: string, aggregatorUrl: string): Promise<CollectionOS[]> {
        let collectionList = [];
        try {
            const params = new HttpParams()
                .set('chain', chain )
                .set('walletAddress', walletAddress);

            const httpOptions = {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                params
            };

            const refreshUrl = `${aggregatorUrl}/magiceden/collections`;
            const response = await firstValueFrom(this.http.get<{ collections: { collection: Object; ownership: Object }[] }>(refreshUrl, httpOptions));

            collectionList = response.collections.map(col => {
                return {
                    name: col.collection['name'],
                    slug: col.collection['slug'],
                    image: col.collection['image'],
                    banner: col.collection['banner'],
                    description: col.collection['description'],
                    collectionAddress: col.collection['primaryContract'],
                };
            });

            return collectionList;
        } catch (error) {
            console.error(`Error fetching EVM collections! WalletAddress: ${walletAddress}`, error);
        }
        return collectionList;
    }

    private async getCollectionsSVM(chain: ChainType, walletAddress: string, aggregatorUrl: string): Promise<CollectionOS[]> {
        let collectionList = [];
        try {
            const params = new HttpParams()
                .set('chain', chain )
                .set('walletAddress', walletAddress);

            const httpOptions = {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                params
            };

            const refreshUrl = `${aggregatorUrl}/magiceden/collections`;
            const response = await firstValueFrom(this.http.get<{ collections: Object[] }>(refreshUrl, httpOptions));

            collectionList = response.collections.map(collection => {
                return {
                    name: collection['collectionName'],
                    slug: collection['collection'],
                    image: collection['image'],
                    banner: collection['banner'],
                    description: collection['description'],
                    collectionAddress: collection['collectionAddress'],
                };
            });

            return collectionList;
        } catch (error) {
            console.error(`Error fetching SVM collections! WalletAddress: ${walletAddress}`, error);
        }
        return collectionList;
    }

    public async refreshNftMetadata(nft: NftAsset): Promise<void> {
        const aggregatorUrl = environment.nftAggregatorUrl;
        if (aggregatorUrl === undefined) throw new Error('No aggregator URL!');

        const tokenId = nft.collectionTokenId;
        if (!tokenId) throw new Error('TokenId is missing!');

        try {
            const { address, chain } = await this.getAddressAndChain();

            let collectionAddress: string;
            if (chain === 'evm') collectionAddress = nft.collectionAddress;
            else if (chain === 'svm') collectionAddress = nft.collectionAddress ? nft.collectionAddress : 'none';
            
            const params = new HttpParams()
                .set('chain', chain )
                .set('walletAddress', address);

            const httpOptions = {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                params
            };

            const refreshUrl = `${aggregatorUrl}/collection/${collectionAddress}/${tokenId}/refresh`;
            await firstValueFrom(this.http.get(refreshUrl, httpOptions));
        } catch (err) {
            console.error('Something went wrong when refreshing the NFT metadata: ', err);
        }
    }

    public connectPhantom(walletAddress: string, session: string): void {
        this.store.dispatch(connectWallet({
            provider: 'phantom',
            chainId: SOLANA_CHAIN_ID,
            address: walletAddress,
            chain: 'solana',
            session,
            connectedAt: Timestamp.now(),
        }));
    }

    public disconnectPhantom(): void {
        this.store.dispatch(disconnectWallet({ chain: 'solana' }));
    }
}

export interface CollectionOS {
    name: string;
    slug: string;
    image: string;
    banner: string;
    description: string;
    collectionAddress: string;
}

interface IWalletStatus {
    phantomEnabled: boolean | null;
    address: string;
    chainId: string
}

type ChainType = 'svm' | 'evm';
